import {BUILDINGS} from "@/types/constants";
import {calculateResourcesPerBlock} from "@/types/utils";

function toNumber(val) {
	if (val == null) return 0;
	if (typeof val === 'number') return val;
	if (typeof val?.toNumber === 'function') return val.toNumber();
	return Number(val) || 0;
}

function getWarehouseCap(village) {
	if (!village?.buildings?.[BUILDINGS["warehouse"]]) return Infinity;
	return Math.floor(1000 * Math.pow(1.229, village.buildings[BUILDINGS["warehouse"]].level));
}

function capResources(resources, cap) {
	return {
		gold: Math.min(toNumber(resources.gold), cap),
		food: Math.min(toNumber(resources.food), cap),
		mana: Math.min(toNumber(resources.mana), cap),
	};
}

export default {
	namespaced: true,
	state: () => ({
		resource_interval: null,
		world: {},
		village: {},
		villageRender: localStorage.getItem("village_render") || "2d",
	}),
	mutations: {
		clear_village(state) {
			state.village = {}
			state.world = {}
		},
		set_village_render(state, render) {
			state.villageRender = render
			localStorage.setItem("village_render", render)
		},
		set_village(state, village) {
			state.village = village
			if (village && village.resources) {
				const cap = getWarehouseCap(village);
				state.village.base_resources = capResources(village.resources, cap);
				state.village.resources = {...state.village.base_resources};
			}
		},
		set_world(state, world) {
			state.world = world
		},
		remove_queue_item(state, {idx, type}) {
			const key = type === 'building' ? 'buildingQueue' : 'troopsQueue'
			if (state.village?.[key]) {
				state.village[key] = state.village[key].filter((_, i) => i !== idx)
			}
		},
		remove_queue_items_batch(state, {indices, type}) {
			const key = type === 'building' ? 'buildingQueue' : 'troopsQueue'
			if (state.village?.[key]) {
				state.village[key] = state.village[key].filter((_, i) => !indices.includes(i))
			}
		},
		set_resources(state, resources) {
			const cap = getWarehouseCap(state.village);
			state.village.resources = capResources(resources, cap);
		}
	},
	actions: {
		async refresh_village({rootState, commit}) {
			if (!rootState.user.active_village)
				return

			try {
				const r = await rootState.user.client.getVillage(rootState.user.active_village)
				commit("set_village", r)
			} catch (e) {
				console.error("refresh_village failed", e)
			}
		},
		simulate_resource_increase({commit, rootState}) {
			if (!rootState.village.village || !rootState.user.client || !rootState.village.village.buildings) {
				return
			}

			const current_resources = rootState.village.village.base_resources;
			if (!current_resources) {
				rootState.village.village.base_resources = capResources(
					rootState.village.village.resources,
					getWarehouseCap(rootState.village.village)
				);
				return
			}

			const resource_factor = rootState.village.world?.settings?.resourceProductionFactor || 1;
			const food_inc = calculateResourcesPerBlock(rootState.village.village.buildings[BUILDINGS["farm"]].level, resource_factor)
			const gold_inc = calculateResourcesPerBlock(rootState.village.village.buildings[BUILDINGS["mine"]].level, resource_factor)
			const mana_inc = calculateResourcesPerBlock(rootState.village.village.buildings[BUILDINGS["mana"]].level, resource_factor)

			const rawLastGathered = rootState.village.village.lastGatheredBlock;
			const last_gathered = typeof rawLastGathered?.toNumber === 'function' ? rawLastGathered.toNumber() : (rawLastGathered || 0);
			const current_slot = rootState.user.client.current_slot;
			if (!last_gathered || !current_slot?.slot) {
				return
			}
			const current_block = current_slot.slot;
			const blocks_passed = current_block - last_gathered;

			const gold_upd = blocks_passed * gold_inc
			const food_upd = blocks_passed * food_inc
			const mana_upd = blocks_passed * mana_inc

			const updated_resources = {
				gold: toNumber(current_resources.gold) + gold_upd,
				food: toNumber(current_resources.food) + food_upd,
				mana: toNumber(current_resources.mana) + mana_upd,
			}
			commit("set_resources", updated_resources)
		}
	}
}
